import { useCallback, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SettingsLayout from "../components/settings/SettingsLayout";
import SettingsSaveBar from "../components/settings/SettingsSaveBar";
import ProfileSection from "../components/settings/sections/ProfileSection";
import PrivacySection from "../components/settings/sections/PrivacySection";
import ThemeSection from "../components/settings/sections/ThemeSection";
import LocationSection from "../components/settings/sections/LocationSection";
import NotificationsSection from "../components/settings/sections/NotificationsSection";
import HelpFeedbackSection from "../components/settings/sections/HelpFeedbackSection";
import LegalSection from "../components/settings/sections/LegalSection";
import AccountSection from "../components/settings/sections/AccountSection";
import ChangePasswordModal from "../components/settings/modals/ChangePasswordModal";
import DeleteAccountModal from "../components/settings/modals/DeleteAccountModal";
import HelpFeedbackModal from "../components/settings/modals/HelpFeedbackModal";
import { useSettings } from "../contexts/SettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import Spinner from "../ui/Spinner";
import { api } from "../api";
import { devLog, logError } from "../utils/logger";

export default function SettingsPage() {
  // Use settings context to fetch and manage settings
  const {
    settings,
    loading,
    saving,
    updateSettings,
    changePassword,
    deleteAccount,
  } = useSettings();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Local form state
  const [form, setForm] = useState(null);
  const scrollContainerRef = useRef(null);

  const [dirty, setDirty] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [showChangePassword, setShowChangePassword] = useState(false);

  // Feedback modal state
  const [showHelpFeedbackModal, setShowHelpFeedbackModal] = useState(false);

  const appVersion = "1.0.0";
  const buildHash = "a1b2c3d";

  // Initialize form from fetched settings
  useEffect(() => {
    if (settings && user) {
      devLog("SettingsPage: Initializing form with settings:", settings);
      devLog("SettingsPage: User data:", user);

      const formData = {
        displayName: user.name || "",
        email: user.email || "",
        emailVerified: user.emailVerified || false,
        avatarUrl: user.avatar || "",
        isPasswordless: false, // Could be added to user object later
        // Provide defaults for settings that may be missing from cache
        showRadiusCircle: true,
        showMyLocationOnMap: true,
        ...settings,
      };

      devLog("SettingsPage: Form initialized with:", formData);
      setForm(formData);
    }
  }, [settings, user]);

  const markDirty = useCallback(() => setDirty(true), []);

  const onChange = useCallback((path, value) => {
    setForm((prev) => {
      const updated = structuredClone(prev);
      const parts = path.split(".");
      let cur = updated;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return updated;
    });
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form) return;

    try {
      // Extract only settings fields (not profile fields)
      const settingsUpdate = {
        defaultVisibility: form.defaultVisibility,
        units: form.units,
        theme: form.theme,
        showMyLocationOnMap: form.showMyLocationOnMap,
        notifications: form.notifications,
        showRadiusCircle: form.showRadiusCircle,
      };

      // Include displayName if it changed
      if (form.displayName !== user.name) {
        settingsUpdate.displayName = form.displayName;
      }

      await updateSettings(settingsUpdate);
      setDirty(false);
      toast.success("Saved", 2000);
    } catch (err) {
      logError("Failed to save settings:", err);
      toast.error("Failed to save settings", 3000);
    }
  }, [form, user, updateSettings, toast]);

  // Unified feedback handler. Errors are intentionally not caught here so
  // they propagate to HelpFeedbackModal, which renders them inline and
  // preserves the user's in-progress description on failure.
  const handleFeedbackSubmit = useCallback(
    async (data) => {
      const { type, category, description } = data;

      if (type === "improvement") {
        await api.submitImprovement({ category, description });
      } else if (type === "bug") {
        await api.submitBugReport({ category, description });
      } else if (type === "support") {
        await api.submitSupportRequest({ category, description });
      }

      toast.success("Thank you! We've received your feedback.", 2000);
    },
    [toast],
  );

  const handlePasswordChange = useCallback(
    async (passwords) => {
      try {
        await changePassword(passwords);
        setShowChangePassword(false);
        toast.success("Password changed successfully", 1800);
      } catch (err) {
        logError("Failed to change password:", err);
        toast.error("Failed to change password", 3000);
      }
    },
    [changePassword, toast],
  );

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteAccount({ password: deleteInput, reason: deleteReason });
      setShowDeleteConfirm(false);
      // Log out after successful deletion
      await logout();
    } catch (err) {
      logError("Failed to delete account:", err);
      toast.error("Failed to delete account");
    } finally {
      setDeleting(false);
    }
  }, [deleteAccount, deleteInput, deleteReason, logout, toast]);

  // Show loading spinner while fetching settings
  if (loading || !form) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface">
        <div className="flex items-center gap-3">
          <Spinner size={32} className="text-primary" />
          <span className="text-text-secondary">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <SettingsLayout scrollContainerRef={scrollContainerRef}>
        <ProfileSection
          form={form}
          onChange={onChange}
          markDirty={markDirty}
          onShowChangePassword={() => setShowChangePassword(true)}
        />

        <PrivacySection form={form} onChange={onChange} />

        <ThemeSection form={form} onChange={onChange} />

        <LocationSection form={form} onChange={onChange} />

        <NotificationsSection form={form} onChange={onChange} />

        <HelpFeedbackSection onOpen={() => setShowHelpFeedbackModal(true)} />

        <LegalSection appVersion={appVersion} buildHash={buildHash} />

        <AccountSection
          onLogout={async () => {
            try {
              await logout();
              navigate("/");
            } catch (err) {
              logError("Logout failed:", err);
              toast.error("Failed to sign out", 3000);
            }
          }}
          onShowDelete={() => {
            setShowDeleteConfirm(true);
            setDeleteStep(1);
          }}
        />
      </SettingsLayout>

      <SettingsSaveBar
        dirty={dirty}
        saving={saving}
        onReview={() =>
          scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
        }
        onSave={handleSave}
      />

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onSubmit={handlePasswordChange}
        />
      )}

      {showDeleteConfirm && (
        <DeleteAccountModal
          step={deleteStep}
          setStep={setDeleteStep}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          deleteReason={deleteReason}
          setDeleteReason={setDeleteReason}
          deleteInput={deleteInput}
          setDeleteInput={setDeleteInput}
          deleting={deleting}
        />
      )}

      {/* Feedback Modal */}
      <HelpFeedbackModal
        open={showHelpFeedbackModal}
        onClose={() => setShowHelpFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        userEmail={user?.email}
      />
    </>
  );
}
